import { useRef, useState } from "react";
import { Camera, CheckCircle2, AlertTriangle, OctagonAlert, HelpCircle, Loader2, Phone, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";

const TIER_META = {
  green:   { label: "Green · Mild — DIY Friendly", color: "#3E7C3C", icon: CheckCircle2 },
  yellow:  { label: "Yellow · Moderate — Monitor & Schedule Service", color: "#C98A1B", icon: AlertTriangle },
  red:     { label: "Red · Urgent — Call Us Immediately", color: "#B3372B", icon: OctagonAlert },
  unknown: { label: "Couldn't Classify — Send It To Us Directly", color: "#2F5F80", icon: HelpCircle },
};

export function SeverityChecker() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const pick = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setError("");
  };

  const reset = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null); setPreview(null); setResult(null); setError(""); setNotes("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("description", notes);
    try {
      const r = await api.post("/guide/severity-check", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const meta = result ? TIER_META[result.tier] || TIER_META.unknown : null;
  const Icon = meta?.icon;

  return (
    <div className="mt-10 bg-white/[0.06] border-2 border-white/15 p-6 md:p-8" data-testid="severity-checker">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 bg-[#9CC4DB] text-[#23282B] inline-flex items-center justify-center shrink-0"><Camera size={20} /></span>
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-white leading-none">Not sure which tier? Snap a photo.</div>
          <p className="text-sm text-white/65 mt-1.5">Upload a picture of the crack, wall or wet spot — our AI gives you an instant severity read. Free, no contact info needed.</p>
        </div>
      </div>

      {!result && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="severity-upload-zone"
            className="border-2 border-dashed border-white/30 hover:border-[#9CC4DB] transition-colors min-h-[160px] flex flex-col items-center justify-center gap-2 text-white/80 p-4"
          >
            {preview ? (
              <img src={preview} alt="Your upload" className="max-h-40 object-contain" />
            ) : (
              <>
                <Camera size={28} className="text-[#9CC4DB]" />
                <span className="font-display text-xl uppercase">Tap to add a photo</span>
                <span className="text-xs text-white/50">JPEG, PNG or WEBP — up to 15MB</span>
              </>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="severity-file-input" onChange={(e) => pick(e.target.files?.[0])} />
          <div className="flex flex-col gap-3">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="severity-notes"
              rows={4}
              placeholder="Optional: tell us what you're seeing (e.g. 'crack appeared after last week's storm')"
              className="flex-1 bg-white/10 border-2 border-white/20 focus:border-[#9CC4DB] focus:outline-none px-3 py-3 text-sm text-white placeholder:text-white/40"
            />
            <button
              onClick={analyze}
              disabled={!file || busy}
              data-testid="severity-analyze-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#9CC4DB] hover:bg-white text-[#23282B] text-sm font-bold tracking-[0.15em] px-6 py-4 uppercase transition-colors disabled:opacity-40"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Analyzing…</> : "Check Severity"}
            </button>
          </div>
        </div>
      )}

      {error && <div data-testid="severity-error" className="mt-4 bg-red-500/15 border-l-4 border-red-400 px-4 py-3 text-sm text-red-100">{error}</div>}

      {result && meta && (
        <div className="mt-6 bg-white p-6 md:p-7" style={{ borderTop: `6px solid ${meta.color}` }} data-testid={`severity-result-${result.tier}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 text-white inline-flex items-center justify-center shrink-0" style={{ background: meta.color }}><Icon size={20} /></span>
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: meta.color }}>{meta.label}</div>
                <div className="font-display text-2xl uppercase text-[#23282B] leading-tight">{result.title || "Assessment"}</div>
              </div>
            </div>
            <button onClick={reset} data-testid="severity-reset" className="p-1.5 text-[#23282B]/50 hover:text-[#23282B]" title="Check another photo"><X size={18} /></button>
          </div>
          <p className="mt-4 text-sm text-[#23282B]/85 leading-relaxed">{result.explanation}</p>
          <p className="mt-2 text-sm font-semibold text-[#23282B]">{result.recommendation}</p>
          {(result.tier === "yellow" || result.tier === "red") && (
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="tel:+12508861112" data-testid="severity-cta-call" className="inline-flex items-center gap-2 text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-opacity hover:opacity-90" style={{ background: meta.color }}>
                <Phone size={14} /> Call (250) 886-1112
              </a>
              <Link to="/contact" data-testid="severity-cta-quote" className="inline-flex items-center gap-2 border-2 border-[#23282B]/25 hover:border-[#2F5F80] hover:text-[#2F5F80] text-[#23282B] text-xs font-bold tracking-[0.15em] px-5 py-2.5 uppercase transition-colors">
                Request an Assessment
              </Link>
            </div>
          )}
          <p className="mt-4 text-[11px] text-[#23282B]/50">AI guidance only — not an engineering assessment. When in doubt, have our crew take a look in person.</p>
        </div>
      )}
    </div>
  );
}
